import { useEffect, useState } from "react";

/** Must match the backend resume copy from ``process_items`` / refresh orchestrators. */
export const RESUME_BANNER_PATTERN = /^Resuming after interruption\b/i;
const DEFAULT_LATCH_MS = 8000;

export interface ResumeBannerLatch {
  show: boolean;
  message: string;
}

/**
 * Keep the "Resuming after interruption" banner visible for a minimum duration
 * even after the backend status message moves on to per-item updates.
 */
export function useResumeBannerLatch(
  statusMessage: string | undefined,
  isActive: boolean,
  latchMs: number = DEFAULT_LATCH_MS,
): ResumeBannerLatch {
  const [latched, setLatched] = useState<{ message: string; until: number } | null>(null);

  useEffect(() => {
    if (!isActive) {
      setLatched(null);
      return;
    }
    if (!statusMessage || !RESUME_BANNER_PATTERN.test(statusMessage)) {
      return;
    }
    const until = Date.now() + latchMs;
    setLatched({ message: statusMessage, until });
  }, [statusMessage, isActive, latchMs]);

  useEffect(() => {
    if (!latched) return;
    const remaining = latched.until - Date.now();
    if (remaining <= 0) {
      setLatched(null);
      return;
    }
    const timer = setTimeout(() => setLatched(null), remaining);
    return () => clearTimeout(timer);
  }, [latched]);

  const show =
    isActive &&
    latched !== null &&
    (Date.now() < latched.until || (!!statusMessage && RESUME_BANNER_PATTERN.test(statusMessage)));

  return {
    show,
    message: latched?.message ?? statusMessage ?? "",
  };
}
